<script setup lang="ts">
const { t: $t } = useI18n()

import type { IBookingService } from '@types'
import TimeDuration from '@booking/components/TimeDuration.vue'
import { getBookingServiceImage } from '@booking/utils/images'
import { usePriceFormatter } from '@booking/utils/prices'

const props = withDefaults(
  defineProps<{
    service?: IBookingService
    defaultImage?: string
  }>(),
  {
    service: () => ({}) as IBookingService,
    defaultImage: '',
  },
)

const { formatPrice } = usePriceFormatter()
const quantity = defineModel<number>('quantity', { default: 0 })

const image = computed(() => getBookingServiceImage(props.service))
const isAvailable = computed(() => !!props.service.availableQuantity)
const maxQuantity = computed(() => props.service.availableQuantity ?? undefined)
const priceText = computed(() => {
  if (!props.service.price) {
    return ''
  }

  const prefix = props.service.price.isFrom ? `${$t('word.from.base')} ` : ''
  return `${prefix}${formatPrice(props.service.price.amount)}`
})

function addToCart() {
  if (isAvailable.value) {
    quantity.value = 1
  }
}
</script>

<template lang="pug">
.inventory-card.flex.col-0.min-w-0.p-6-6-0.br-20(data-id="inventory-card" :data-service-id="service.id" :class="{ 'o-1': !isAvailable }")
  UiImage.inventory-image.w-100p.br-16(
    contain
    :src="image"
    :showZoom="false"
    :placeholder="defaultImage"
  )
  .inventory-card-content.flex-between-unset.col-8.p-12-6
    .flex.col-4.min-w-0
      .cost.flex.row-6.align-items-center.white-space-nowrap(v-if="service.price")
        span.price.typ-12-18-600(class="md_typ-14-20-600") {{ priceText }}
        span.duration.typ-11-16-400(v-if="service.price.durationMinutes" class="md_typ-12-18-400")
          | {{ $t('page.services.inventory.price_period') }}&nbsp;
          TimeDuration(:m="service.price.durationMinutes")
      .inventory-title.typ-12-18-500.h-36.ellipsis-2(class="md_h-40 md_typ-14-20-500") {{ service.title }}
    UiButton.w-100p(
      v-if="!service?.availableQuantity"
      disabled
      label="page.services.inventory.unavailable"
      :variant="ButtonVariant.Secondary"
      :size="Sizes.Leia"
    )
    UiInputNumber.w-100p(
      v-else-if="quantity > 0"
      v-model="quantity"
      data-id="inventory-quantity"
      stepper
      :size="Sizes.Leia"
      :background="Background.White"
      :min="0"
      :max="maxQuantity"
    )
    UiButton.w-100p(
      v-else
      data-id="inventory-add"
      :label="isAvailable ? 'page.services.inventory.book' : 'page.services.inventory.unavailable'"
      :variant="ButtonVariant.Secondary"
      :size="Sizes.Leia"
      :disabled="!isAvailable"
      @click="addToCart"
    )
</template>

<style scoped>
.inventory-card {
  background: var(--cue-bg-neutral-secondary);
}

.inventory-image {
  aspect-ratio: 1;
  background: var(--cue-core-neutral-0);
}

.inventory-card-content {
  --uno: flex-1;
}

.price,
.inventory-title {
  color: var(--cue-content-neutral-primary);
}

.duration {
  color: var(--cue-content-neutral-secondary);
}
</style>
